import axios from "axios";
import {
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
} from "firebase/auth";
import { auth, googleProvider, appleProvider } from "../../firebase";

// Same backend the mobile app authenticates against (homeowner-backend/user-login),
// backed by the same Cosmos DB users collection — any account created or logged
// into on the app can sign in here, and vice versa.
const isLocal =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

const isStagingHost =
  typeof window !== "undefined" && window.location.hostname.includes("staging");

export const DV_API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && localStorage.getItem("dv_api_url")) ||
  (isLocal
    ? "http://localhost:7071/api"
    : isStagingHost
      ? "https://homeowner-staging.azurewebsites.net/api"
      : "https://homeowner-backend.azurewebsites.net/api");

const STORAGE_KEY = "dv_session";

export interface DVSession {
  token: string;
  email: string;
  fullName: string;
}

export function getSession(): DVSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token) return null;
    return parsed as DVSession;
  } catch {
    return null;
  }
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export type LoginResult = {
  session?: DVSession;
  requiresVerification?: boolean;
  email?: string;
  message?: string;
};

export async function login(
  email: string,
  password: string,
): Promise<LoginResult> {
  let res;
  try {
    res = await axios.post(
      `${DV_API_BASE}/users/login`,
      { email, password },
      {
        headers: {
          "Content-Type": "application/json",
          "x-client-platform": "web",
        },
      },
    );
  } catch (err: any) {
    const data = err?.response?.data;
    if (
      data?.requiresVerification ||
      data?.message?.includes("verify your email")
    ) {
      return {
        requiresVerification: true,
        email: data?.email || email,
        message:
          data?.message ||
          "Please verify your email address. A 4-digit verification code has been sent to your email.",
      };
    }
    const message = data?.message || err?.message;
    throw new Error(message || "Could not sign in. Please try again.");
  }

  const data = res.data;
  if (data?.requiresVerification) {
    return {
      requiresVerification: true,
      email: data.email || email,
      message:
        data.message ||
        "Please verify your email address. A 4-digit verification code has been sent to your email.",
    };
  }

  if (!data?.jwtToken) {
    throw new Error(data?.message || "Could not sign in. Please try again.");
  }

  const session: DVSession = {
    token: data.jwtToken,
    email: data.email || email,
    fullName: data.fullName || email,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return { session };
}

export async function loginWithSocial(
  provider: "google" | "apple",
  token: string,
  email?: string,
  userIdentifier?: string,
  name?: string,
): Promise<DVSession> {
  let res;
  try {
    res = await axios.post(
      `${DV_API_BASE}/users/socialauth`,
      {
        provider,
        token,
        email,
        userIdentifier,
        name,
      },
      { headers: { "Content-Type": "application/json" } },
    );
  } catch (err: any) {
    const message = err?.response?.data?.message || err?.response?.data?.error;
    throw new Error(
      message || `Could not sign in with ${provider}. Please try again.`,
    );
  }

  const data = res.data?.userData || res.data;
  if (!data?.jwtToken) {
    throw new Error(data?.message || `Could not sign in with ${provider}.`);
  }

  const session: DVSession = {
    token: data.jwtToken,
    email: data.email || email || "",
    fullName: data.fullName || name || data.email || email || "User",
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  return session;
}

export async function loginWithGoogle(): Promise<DVSession> {
  if (!auth || !googleProvider) {
    throw new Error(
      "Google Sign-In is temporarily unavailable. Please sign in with email and password.",
    );
  }
  if (isIosBrowser()) {
    await signInWithRedirect(auth, googleProvider);
    return new Promise<DVSession>(() => undefined);
  }
  const result = await signInWithPopup(auth, googleProvider);
  const firebaseToken = await result.user.getIdToken();

  return await loginWithSocial(
    "google",
    firebaseToken,
    result.user.email || undefined,
    result.user.uid,
    result.user.displayName || undefined,
  );
}

export async function loginWithGoogleIdToken(
  idToken: string,
): Promise<DVSession> {
  return await loginWithSocial("google", idToken);
}

export async function loginWithAppleIdToken(
  idToken: string,
  email?: string,
  name?: string,
): Promise<DVSession> {
  return await loginWithSocial("apple", idToken, email, undefined, name);
}

export async function loginWithApple(): Promise<DVSession> {
  if (!auth || !appleProvider) {
    throw new Error(
      "Apple Sign-In is temporarily unavailable. Please sign in with email and password.",
    );
  }
  if (isIosBrowser()) {
    await signInWithRedirect(auth, appleProvider);
    return new Promise<DVSession>(() => undefined);
  }
  const result = await signInWithPopup(auth, appleProvider);
  const firebaseToken = await result.user.getIdToken();

  return await loginWithSocial(
    "apple",
    firebaseToken,
    result.user.email || undefined,
    result.user.uid,
    result.user.displayName || undefined,
  );
}

function isIosBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export async function completeSocialRedirect(): Promise<DVSession | null> {
  if (!auth) return null;
  const result = await getRedirectResult(auth);
  if (!result?.user) return null;

  const providerId = result.user.providerData[0]?.providerId || "google.com";
  const provider = providerId === "apple.com" ? "apple" : "google";
  const firebaseToken = await result.user.getIdToken();
  return loginWithSocial(
    provider,
    firebaseToken,
    result.user.email || undefined,
    result.user.uid,
    result.user.displayName || undefined,
  );
}

export async function signup(
  fullName: string,
  email: string,
  password: string,
): Promise<{
  success: boolean;
  message: string;
  requiresVerification?: boolean;
}> {
  try {
    const res = await axios.post(
      `${DV_API_BASE}/users`,
      { fullName, email, password },
      {
        headers: {
          "Content-Type": "application/json",
          "x-client-platform": "web",
        },
      },
    );
    return res.data;
  } catch (err: any) {
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Could not create account. Please try again.";
    throw new Error(message);
  }
}

export async function verifyEmailCode(
  email: string,
  code: string | number,
): Promise<DVSession> {
  try {
    const res = await axios.post(
      `${DV_API_BASE}/user-verify-email`,
      { email, code },
      { headers: { "Content-Type": "application/json" } },
    );
    const data = res.data;
    if (!data?.jwtToken) {
      throw new Error(
        data?.message ||
          "Verification succeeded but session could not be established.",
      );
    }
    const session: DVSession = {
      token: data.jwtToken,
      email: data.email || email,
      fullName: data.fullName || email,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    return session;
  } catch (err: any) {
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Verification failed. Please check the code and try again.";
    throw new Error(message);
  }
}

export async function resendVerificationCode(email: string): Promise<void> {
  try {
    await axios.post(
      `${DV_API_BASE}/users/resend-verification`,
      { email },
      {
        headers: {
          "Content-Type": "application/json",
          "x-client-platform": "web",
        },
      },
    );
  } catch (err: any) {
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Could not resend verification email.";
    throw new Error(message);
  }
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
